import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Type, Image as ImageIcon, Square, Cloud, Trash2, Copy, Lock, Unlock,
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, Minus, Plus,
  MoveDown, MoveUp, Settings as SettingsIcon, Wand2, Loader2, Sparkles,
  RotateCw, Triangle, Star, Heart, Hexagon, ChevronRight, ArrowRight,
  Layers, Eye, EyeOff, X as XIcon, GripVertical,
  Film, Volume2, VolumeX, RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { CanvasRenderer } from "./CanvasRenderer";
import { CANVAS_W, CANVAS_H, fontCss, FONT_FAMILIES, defaultElement, newId } from "./canvasModel";
import WidgetSettings from "./WidgetSettings";
import { defaultWidgetConfig } from "./WidgetOverlay";
import ImagePicker from "./ImagePicker";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PRESET_COLORS = [
  "#FFFFFF", "#000000", "#0F172A", "#1E293B", "#475569", "#94A3B8",
  "#FEE2E2", "#FCE7F3", "#FEF3C7", "#DCFCE7", "#DBEAFE", "#E0F2FE",
  "#DC2626", "#DB2777", "#F59E0B", "#16A34A", "#2563EB", "#0EA5E9",
  "#7C3AED", "#0F766E", "#B91C1C", "#7C2D12", "#9D174D", "#365314",
];

/**
 * Direct-manipulation canvas editor (Canva / OptiSigns style).
 *
 * Props:
 *   elements: array of element objects
 *   onChange: (newElements) => void
 *   background: canvas background color
 */
export default function CanvasEditor({
  elements = [],
  onChange,
  background = "#FFFFFF",
  onPushToScreen,
  designTitle = "",
  canvasW = CANVAS_W,
  canvasH = CANVAS_H,
  onCanvasSizeChange,
}) {
  const [selectedId, setSelectedId] = useState(null);
  const [editingTextId, setEditingTextId] = useState(null);
  const [imagePicker, setImagePicker] = useState({ open: false, replaceId: null });
  const [layersOpen, setLayersOpen] = useState(false);
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const [scale, setScale] = useState(0.4);

  // Compute auto-fit scale
  useEffect(() => {
    const compute = () => {
      const w = containerRef.current?.clientWidth || 800;
      const h = containerRef.current?.clientHeight || 500;
      // Leave 60px padding for handle headroom
      setScale(Math.min((w - 60) / canvasW, (h - 60) / canvasH));
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [canvasW, canvasH]);

  const selected = useMemo(() => elements.find((e) => e.id === selectedId), [elements, selectedId]);

  // ----- mutation helpers -----
  const updateOne = useCallback((id, patch) => {
    onChange(elements.map((e) => e.id === id ? { ...e, ...patch } : e));
  }, [elements, onChange]);

  const addElement = (kind, extra = {}) => {
    // Auto-offset if a similar default-position element already exists
    let el = defaultElement(kind, extra);
    const sameKind = elements.filter((e) => e.kind === kind);
    if (sameKind.length > 0) {
      const offset = sameKind.length * 30;
      el = { ...el, x: (extra.x ?? el.x) + offset, y: (extra.y ?? el.y) + offset };
    }
    onChange([...elements, el]);
    setSelectedId(el.id);
  };

  const duplicateEl = (id) => {
    const src = elements.find((e) => e.id === id);
    if (!src) return;
    const copy = { ...src, id: newId(src.kind), x: src.x + 40, y: src.y + 40 };
    onChange([...elements, copy]);
    setSelectedId(copy.id);
  };

  const removeEl = (id) => {
    onChange(elements.filter((e) => e.id !== id));
    setSelectedId(null);
  };

  const changeZ = (id, dir) => {
    const list = [...elements].sort((a, b) => (a.z ?? 0) - (b.z ?? 0));
    const idx = list.findIndex((e) => e.id === id);
    if (idx < 0) return;
    if (dir === "up" && idx === list.length - 1) return;
    if (dir === "down" && idx === 0) return;
    const target = dir === "up" ? list[idx + 1] : list[idx - 1];
    const cur = list[idx];
    onChange(elements.map((e) =>
      e.id === cur.id ? { ...e, z: target.z ?? 0 }
      : e.id === target.id ? { ...e, z: cur.z ?? 0 }
      : e
    ));
  };

  // ----- apply brand kit -----
  const applyBrand = useCallback((kit) => {
    if (!kit) return 0;
    const primary   = kit.primary   || "#0EA5E9";
    const secondary = kit.secondary || "#0F172A";
    const accent    = kit.accent    || "#F59E0B";
    const dark      = kit.dark      || "#0F172A";
    const fontHead  = kit.font_heading || null;
    const fontBody  = kit.font_body    || null;
    let changed = 0;
    let largestFs = 0;
    elements.forEach((e) => {
      if (e.kind === "text") largestFs = Math.max(largestFs, e.fontSize || 0);
    });
    let next = elements.map((e) => {
      if (e.kind === "text") {
        const isHeadline = (e.fontSize || 0) >= Math.max(48, largestFs * 0.7);
        const colour = isHeadline ? primary : secondary;
        const font   = isHeadline ? (fontHead || e.fontFamily) : (fontBody || e.fontFamily);
        if (e.color !== colour || e.fontFamily !== font) changed++;
        return { ...e, color: colour, fontFamily: font };
      }
      if (e.kind === "shape") {
        // Cycle shapes through accent/primary/dark for visual rhythm
        const palette = [accent, primary, dark];
        const idx = elements.filter((x) => x.kind === "shape").indexOf(e) % palette.length;
        const newFill = palette[idx];
        if (e.fill !== newFill) changed++;
        return { ...e, fill: newFill };
      }
      return e;
    });
    // Auto-add logo if there isn't an image element flagged as a logo yet
    const hasLogo = elements.some((e) => e.kind === "image" && e._isBrandLogo);
    if (kit.logo_url && !hasLogo) {
      // Use backend URL or relative URL — both work in <img>
      next = [...next, {
        ...defaultElement("image", {
          src: kit.logo_url,
          x: 60, y: 60, w: 220, h: 220,
          fit: "contain",
          _isBrandLogo: true,
          z: 999,
        })
      }];
      changed++;
    }
    onChange(next);
    return changed;
  }, [elements, onChange]);

  // ----- drag/resize -----
  const dragState = useRef(null);
  // Active alignment guides while dragging (thin dashed lines that show which
  // edges/centers of the dragged element are lined up with either the canvas
  // or another element). Cleared on mouseup.
  const [snapGuides, setSnapGuides] = useState([]);
  // Snap tolerance in canvas coords — small enough to feel intentional, big
  // enough to catch a fast drag. ~8px at 1920 width ≈ 0.4% of the canvas.
  const SNAP_TOL = 8;

  /**
   * Compute the guide lines to display + the element x/y snapped to any
   * alignment within tolerance. Snap targets:
   *   • Canvas: left / centre / right, top / middle / bottom
   *   • Every other element: left / centre / right, top / middle / bottom
   * A guide is emitted for every axis that ends up perfectly aligned.
   */
  const computeSnap = (proposedX, proposedY, targetEl) => {
    const others = elements.filter((e) => e.id !== targetEl.id);
    // Build vertical snap candidates (x positions) + horizontal (y positions).
    const vTargets = [
      { pos: 0,              kind: "canvas-left"   },
      { pos: canvasW / 2,    kind: "canvas-vmid"   },
      { pos: canvasW,        kind: "canvas-right"  },
    ];
    const hTargets = [
      { pos: 0,              kind: "canvas-top"    },
      { pos: canvasH / 2,    kind: "canvas-hmid"   },
      { pos: canvasH,        kind: "canvas-bottom" },
    ];
    for (const o of others) {
      vTargets.push({ pos: o.x,               kind: "el-left",   ref: o });
      vTargets.push({ pos: o.x + o.w / 2,     kind: "el-vmid",   ref: o });
      vTargets.push({ pos: o.x + o.w,         kind: "el-right",  ref: o });
      hTargets.push({ pos: o.y,               kind: "el-top",    ref: o });
      hTargets.push({ pos: o.y + o.h / 2,     kind: "el-hmid",   ref: o });
      hTargets.push({ pos: o.y + o.h,         kind: "el-bottom", ref: o });
    }
    // Points of the dragged element itself we want to align.
    const w = targetEl.w, h = targetEl.h;
    const vSelf = [
      { at: proposedX,             role: "left"   },
      { at: proposedX + w / 2,     role: "vmid"   },
      { at: proposedX + w,         role: "right"  },
    ];
    const hSelf = [
      { at: proposedY,             role: "top"    },
      { at: proposedY + h / 2,     role: "hmid"   },
      { at: proposedY + h,         role: "bottom" },
    ];
    // Find the closest vertical snap (lowest delta, within tolerance).
    let vBest = null;
    for (const s of vSelf) {
      for (const t of vTargets) {
        const d = t.pos - s.at;
        if (Math.abs(d) < SNAP_TOL && (!vBest || Math.abs(d) < Math.abs(vBest.d))) {
          vBest = { d, guide: { axis: "v", pos: t.pos, kind: t.kind } };
        }
      }
    }
    let hBest = null;
    for (const s of hSelf) {
      for (const t of hTargets) {
        const d = t.pos - s.at;
        if (Math.abs(d) < SNAP_TOL && (!hBest || Math.abs(d) < Math.abs(hBest.d))) {
          hBest = { d, guide: { axis: "h", pos: t.pos, kind: t.kind } };
        }
      }
    }
    const snappedX = vBest ? proposedX + vBest.d : proposedX;
    const snappedY = hBest ? proposedY + hBest.d : proposedY;
    // Emit a guide line for every axis that fully-aligned after snapping.
    // Also detect same-size alignment (equal-width/equal-height) to help users
    // build UIs where two elements share dimensions.
    const guides = [];
    if (vBest) guides.push(vBest.guide);
    if (hBest) guides.push(hBest.guide);
    for (const o of others) {
      if (Math.abs(o.w - w) < 1) guides.push({ axis: "size", kind: "same-w", ref: o });
      if (Math.abs(o.h - h) < 1) guides.push({ axis: "size", kind: "same-h", ref: o });
    }
    return { x: snappedX, y: snappedY, guides };
  };

  const onElementMouseDown = (e, el, mode = "move", handle = null) => {
    if (el.locked) return;
    e.stopPropagation();
    e.preventDefault();
    if (editingTextId === el.id) return;
    setSelectedId(el.id);
    // Capture pointer so touch drags don't get lost as the finger crosses
    // element boundaries.
    if (e.pointerId != null && e.currentTarget?.setPointerCapture) {
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ok */ }
    }
    dragState.current = {
      id: el.id,
      mode,
      handle,
      startX: e.clientX,
      startY: e.clientY,
      origX: el.x, origY: el.y, origW: el.w, origH: el.h,
      // Font-size scale-with-resize (text elements only, corner handles only).
      origFontSize: el.kind === "text" ? el.fontSize : undefined,
      // Corner handles snap to proportional resize by default.
      isCorner: mode === "resize" && handle && handle.length === 2,
    };
    document.addEventListener("pointermove", onMouseMove);
    document.addEventListener("pointerup",   onMouseUp);
    document.addEventListener("pointercancel", onMouseUp);
  };

  const onMouseMove = (e) => {
    const s = dragState.current;
    if (!s) return;
    const dx = (e.clientX - s.startX) / scale;
    const dy = (e.clientY - s.startY) / scale;
    const target = elements.find((x) => x.id === s.id);
    if (!target) return;
    if (s.mode === "move") {
      const proposedX = Math.round(s.origX + dx);
      const proposedY = Math.round(s.origY + dy);
      // Hold Alt while dragging to bypass snap.
      if (e.altKey) {
        updateOne(s.id, { x: proposedX, y: proposedY });
        setSnapGuides([]);
        return;
      }
      const snapped = computeSnap(proposedX, proposedY, target);
      updateOne(s.id, {
        x: Math.round(snapped.x),
        y: Math.round(snapped.y),
      });
      setSnapGuides(snapped.guides);
      return;
    }
    if (s.mode === "resize") {
      const minW = 32, minH = 32;
      let { origX: x, origY: y, origW: w, origH: h } = s;

      // Corner drag → proportional resize.
      // Shift while dragging an edge → also proportional (for parity with Canva).
      const proportional = s.isCorner || e.shiftKey;

      if (proportional && s.isCorner) {
        // Determine the aspect ratio from the ORIGINAL size, then pick
        // the dominant axis of the drag and scale both dims to match.
        const aspect = s.origW / Math.max(1, s.origH);
        // Compute an "effective delta" that respects which corner is being
        // dragged. For NW / SE, positive dx+dy grows the box; for NE / SW,
        // dx grows and dy shrinks (or vice versa) so we combine signed
        // deltas by the corner's expanding direction.
        const kx = s.handle.includes("e") ? 1 : -1;
        const ky = s.handle.includes("s") ? 1 : -1;
        const growX = dx * kx;
        const growY = dy * ky;
        // Pick the larger axis so the box always keeps aspect.
        const grow = Math.max(growX, growY * aspect);
        const newW = Math.max(minW, s.origW + grow);
        const newH = Math.max(minH, newW / aspect);
        // Recompute x/y based on which corner is being dragged.
        w = newW; h = newH;
        if (s.handle.includes("w")) x = s.origX + (s.origW - newW);
        if (s.handle.includes("n")) y = s.origY + (s.origH - newH);
      } else {
        if (s.handle.includes("e")) w = Math.max(minW, s.origW + dx);
        if (s.handle.includes("s")) h = Math.max(minH, s.origH + dy);
        if (s.handle.includes("w")) { w = Math.max(minW, s.origW - dx); x = s.origX + (s.origW - w); }
        if (s.handle.includes("n")) { h = Math.max(minH, s.origH - dy); y = s.origY + (s.origH - h); }
      }
      const patch = { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) };
      // For text elements resized from a CORNER, scale the font size
      // proportionally so the words grow/shrink with the box.
      if (s.origFontSize && s.isCorner) {
        const ratio = w / Math.max(1, s.origW);
        patch.fontSize = Math.max(8, Math.round(s.origFontSize * ratio));
      }
      updateOne(s.id, patch);
    }
  };

  const onMouseUp = () => {
    dragState.current = null;
    setSnapGuides([]);
    document.removeEventListener("pointermove", onMouseMove);
    document.removeEventListener("pointerup",   onMouseUp);
    document.removeEventListener("pointercancel", onMouseUp);
  };

  // Cleanup on unmount
  useEffect(() => () => {
    document.removeEventListener("pointermove", onMouseMove);
    document.removeEventListener("pointerup",   onMouseUp);
    document.removeEventListener("pointercancel", onMouseUp);
  }, []);

  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if (!selectedId) return;
      if (e.target && (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable)) return;
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        removeEl(selectedId);
        e.preventDefault();
      }
      // Nudge with arrows
      const step = e.shiftKey ? 20 : 4;
      const el = elements.find((x) => x.id === selectedId);
      if (!el) return;
      if (e.key === "ArrowUp")    updateOne(selectedId, { y: el.y - step });
      if (e.key === "ArrowDown")  updateOne(selectedId, { y: el.y + step });
      if (e.key === "ArrowLeft")  updateOne(selectedId, { x: el.x - step });
      if (e.key === "ArrowRight") updateOne(selectedId, { x: el.x + step });
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedId, elements, updateOne]);

  // Order: by z
  const ordered = useMemo(() => [...elements].sort((a, b) => (a.z ?? 0) - (b.z ?? 0)), [elements]);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* TOP TOOLBAR — add-element + Push to Screen + Apply Brand only */}
      <TopToolbar
        addElement={addElement}
        onPushToScreen={onPushToScreen}
        onOpenImagePicker={(replaceId = null) => setImagePicker({ open: true, replaceId })}
        applyBrand={applyBrand}
        layersOpen={layersOpen}
        onToggleLayers={() => setLayersOpen((v) => !v)}
      />

      {/* CONTEXT TOOLBAR — docked second row that reflects the current selection */}
      <ContextToolbar
        el={selected}
        update={(patch) => selected && updateOne(selected.id, patch)}
        onDuplicate={() => selected && duplicateEl(selected.id)}
        onDelete={() => selected && removeEl(selected.id)}
        onChangeZ={(dir) => selected && changeZ(selected.id, dir)}
        onLockToggle={() => selected && updateOne(selected.id, { locked: !selected.locked })}
        onReplaceImage={() => selected && setImagePicker({ open: true, replaceId: selected.id })}
        designTitle={designTitle}
        elements={elements}
      />

      {/* CANVAS STAGE + LAYERS PANEL — flex row so the panel can slide in from the right */}
      <div className="flex-1 min-h-0 flex relative">
        <div
          ref={containerRef}
          className="flex-1 min-h-0 overflow-hidden relative grid place-items-center"
          style={{
            background: "#F1F5F9",
            backgroundImage: "radial-gradient(circle, #CBD5E1 1px, transparent 1px)",
            backgroundSize: "20px 20px",
          }}
          onPointerDown={() => { setSelectedId(null); setEditingTextId(null); }}
      >
        <div
          ref={stageRef}
          className="relative shadow-2xl"
          style={{
            width: canvasW * scale,
            height: canvasH * scale,
            background,
            transform: "translateZ(0)",
            touchAction: "none",
          }}
          onPointerDown={() => { setSelectedId(null); setEditingTextId(null); }}
        >
          {/* Underlying renderer for non-selected elements (visual only).
              Hide the element currently being text-edited so it doesn't
              double-render behind the editing textarea. */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ transform: `scale(${scale})`, transformOrigin: "0 0", width: canvasW, height: canvasH }}
          >
            <CanvasRenderer
              elements={elements}
              background={background}
              scaleToFit={false}
              hideIds={editingTextId ? [editingTextId] : []}
              canvasW={canvasW}
              canvasH={canvasH}
            />
          </div>

          {/* Interactive layer: per-element hit boxes with handles */}
          <div
            className="absolute inset-0"
            style={{ transform: `scale(${scale})`, transformOrigin: "0 0", width: canvasW, height: canvasH }}
          >
            {ordered.map((el) => (
              <ElementHitBox
                key={el.id}
                el={el}
                selected={selectedId === el.id}
                editing={editingTextId === el.id}
                onMouseDown={(e) => onElementMouseDown(e, el, "move")}
                onResizeMouseDown={(e, handle) => onElementMouseDown(e, el, "resize", handle)}
                onDoubleClick={() => el.kind === "text" && setEditingTextId(el.id)}
                onTextEdit={(text) => updateOne(el.id, { text })}
                onTextEditDone={() => setEditingTextId(null)}
                scale={scale}
              />
            ))}
            {/* Smart alignment guides — rendered ONLY while dragging. Vanish
                the moment the user releases so they never clutter the design. */}
            <SnapGuides guides={snapGuides} canvasW={canvasW} canvasH={canvasH} scale={scale} />
            {/* Floating quick-action pill (Delete · Rotation · Lock · Copy).
                Rendered OUTSIDE ElementHitBox so it doesn't rotate with the
                element — it always stays upright and visually below the
                element's bounding box. */}
            {selected && !editingTextId && (
              <QuickActionPill
                el={selected}
                scale={scale}
                canvasH={canvasH}
                stageRef={stageRef}
                onDelete={() => removeEl(selected.id)}
                onDuplicate={() => duplicateEl(selected.id)}
                onLockToggle={() => updateOne(selected.id, { locked: !selected.locked })}
                onRotate={(rot) => updateOne(selected.id, { rotation: rot })}
              />
            )}
          </div>
        </div>

        {/* Scale indicator */}
        <div className="absolute bottom-3 right-3 bg-white border border-slate-200 rounded-full px-3 py-1 text-xs font-bold text-slate-600 shadow" data-testid="canvas-zoom">
          {Math.round(scale * 100)}%
        </div>
      </div>

      {/* LAYERS PANEL — slides in from the right when the Layers button is toggled */}
      {layersOpen && (
        <LayersPanel
          elements={elements}
          selectedId={selectedId}
          onSelect={(id) => setSelectedId(id)}
          onClose={() => setLayersOpen(false)}
          onReorder={(newOrder) => {
            // newOrder is the array of element ids from TOP-most layer to bottom.
            // Element `z` orders from smallest → biggest visually. Convert the
            // top-first list into a z value per id.
            const zMap = new Map();
            const n = newOrder.length;
            newOrder.forEach((id, idx) => zMap.set(id, n - idx));
            onChange(elements.map((el) => ({ ...el, z: zMap.get(el.id) ?? el.z })));
          }}
          onToggleLock={(id) => {
            const el = elements.find((x) => x.id === id);
            if (el) updateOne(id, { locked: !el.locked });
          }}
          onDelete={(id) => removeEl(id)}
          onDuplicate={(id) => duplicateEl(id)}
          onRename={(id, name) => updateOne(id, { layerName: name })}
        />
      )}
      </div>{/* /flex row */}

      {/* Image picker dialog — used for both Add Image and Replace Image */}
      <ImagePicker
        open={imagePicker.open}
        onClose={() => setImagePicker({ open: false, replaceId: null })}
        onPick={(url, kind = "image") => {
          if (imagePicker.replaceId) {
            // Replacing an existing element — update the src AND change the
            // element's kind if the customer swapped an image for a video
            // (or vice versa). Without changing `kind` the canvas would try
            // to render an <img> pointing at an MP4 and show a broken icon.
            updateOne(imagePicker.replaceId, { src: url, kind });
          } else {
            addElement(kind, { src: url });
          }
        }}
      />
    </div>
  );
}

// ============================================================================
// ContextToolbar — the second row of the editor header. Shows contextual
// controls for the currently selected element (font/color/rotate for text,
// shape picker + rotate for shapes, change/remove-bg/rotate for images) plus
// z-order + duplicate + lock + delete on the right. It's docked above the
// canvas (not floating over the artwork), so it never blocks the design.
// A friendly "click any layer to edit" hint is shown when nothing is selected
// so the row's height stays constant — no vertical shift on selection change.
// ============================================================================
function ContextToolbar({
  el,
  update, onDuplicate, onDelete, onChangeZ, onLockToggle, onReplaceImage,
  designTitle = "", elements = [],
}) {
  return (
    <div
      className="border-b border-slate-200 bg-white/95 backdrop-blur-sm px-3 h-12 flex items-center gap-1 sticky top-[52px] z-[9] overflow-x-auto"
      data-testid="context-toolbar"
      onPointerDown={(e) => e.stopPropagation() /* don't deselect on toolbar clicks */}
    >
      {el ? (
        <>
          {el.kind === "text"   && <TextToolbar   el={el} update={update} designTitle={designTitle} elements={elements} />}
          {el.kind === "shape"  && <ShapeToolbar  el={el} update={update} />}
          {el.kind === "image"  && <ImageToolbar  el={el} update={update} onReplaceImage={onReplaceImage} />}
          {el.kind === "video"  && <VideoToolbar  el={el} update={update} onReplaceImage={onReplaceImage} />}
          {el.kind === "widget" && <WidgetToolbar el={el} update={update} />}

          {/* Right-aligned element actions */}
          <div className="flex-1" />
          <ToolButton title="Send backward" onClick={() => onChangeZ("down")} testid="z-down"><MoveDown className="h-4 w-4" /></ToolButton>
          <ToolButton title="Bring forward" onClick={() => onChangeZ("up")}   testid="z-up"><MoveUp className="h-4 w-4" /></ToolButton>
          <ToolButton title="Duplicate"     onClick={onDuplicate}             testid="el-duplicate"><Copy className="h-4 w-4" /></ToolButton>
          <ToolButton title={el.locked ? "Unlock" : "Lock"} onClick={onLockToggle} testid="el-lock">
            {el.locked ? <Lock className="h-4 w-4 text-rose-500" /> : <Unlock className="h-4 w-4" />}
          </ToolButton>
          <ToolButton title="Delete" onClick={onDelete} testid="el-delete">
            <Trash2 className="h-4 w-4 text-rose-500" />
          </ToolButton>
        </>
      ) : (
        <div className="text-xs text-slate-400 px-2 select-none" data-testid="context-toolbar-hint">
          Click any layer to edit — text, colours, size, rotation, and effects appear here.
        </div>
      )}
    </div>
  );
}
// ============================================================================
// TOP TOOLBAR — global controls only (add-element, Push to Screen, Apply Brand)
// Contextual element controls (TextToolbar/ShapeToolbar/…) now render in a
// separate `ContextToolbar` (docked second row) — see below.
// ============================================================================
function TopToolbar({ addElement, onPushToScreen, onOpenImagePicker, applyBrand, layersOpen, onToggleLayers }) {
  return (
    <div className="border-b border-slate-200 bg-white px-3 py-2 flex flex-wrap items-center gap-2 sticky top-0 z-10" data-testid="canvas-toolbar">
      {/* Add element buttons */}
      <div className="flex items-center gap-1">
        <ToolButton title="Add text"   onClick={() => addElement("text")}  testid="add-text"><Type className="h-4 w-4" /></ToolButton>
        <ToolButton title="Add image"  onClick={() => onOpenImagePicker(null)} testid="add-image"><ImageIcon className="h-4 w-4" /></ToolButton>
        <AddShapePicker addElement={addElement} />
        <Popover>
          <PopoverTrigger asChild>
            <button title="Add widget" className="h-8 w-8 rounded-md inline-flex items-center justify-center transition-colors text-slate-700 hover:bg-slate-100" data-testid="add-widget">
              <Cloud className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2 bg-white border border-slate-200" align="start">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 px-2 py-1.5">Add widget</div>
            {[
              { type: "weather",   label: "Weather",      sizes: { w: 520, h: 280 } },
              { type: "clock",     label: "Clock & Date", sizes: { w: 460, h: 200 } },
              { type: "qr",        label: "QR Code",      sizes: { w: 520, h: 240 } },
              { type: "countdown", label: "Countdown",    sizes: { w: 640, h: 240 } },
              { type: "ticker",    label: "News Ticker",  sizes: { w: 1920, h: 100, x: 0, y: 980 } },
            ].map((w) => (
              <button
                key={w.type}
                onClick={() => addElement("widget", { widgetType: w.type, widgetConfig: defaultWidgetConfig(w.type), ...w.sizes })}
                className="w-full text-left px-2 py-2 hover:bg-slate-50 rounded-md text-sm text-slate-700"
                data-testid={`add-widget-${w.type}`}
              >
                {w.label}
              </button>
            ))}
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex-1" />

      {/* Layers button — toggles the right slide-out panel */}
      <button
        onClick={onToggleLayers}
        className={`h-8 px-3 rounded-full text-xs font-semibold transition-colors inline-flex items-center gap-1.5 ${
          layersOpen
            ? "bg-slate-900 text-white"
            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
        }`}
        data-testid="layers-toggle-btn"
        title="Layers"
      >
        <Layers className="h-3.5 w-3.5" />
        Layers
      </button>

      {onPushToScreen && (
        <Button onClick={onPushToScreen} className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm h-8 px-4 text-xs" data-testid="push-to-screen-btn">
          Push to Screen
        </Button>
      )}
      <ApplyBrandButton applyBrand={applyBrand} />
    </div>
  );
}

function ToolButton({ children, onClick, title, active, testid }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      data-testid={testid}
      className={`h-8 w-8 rounded-md inline-flex items-center justify-center transition-colors ${
        active ? "bg-sky-100 text-sky-700" : "text-slate-700 hover:bg-slate-100"
      }`}
    >
      {children}
    </button>
  );
}

// ----------------------------------------------------------------------------
// Apply Brand — one-click recolour the canvas with the user's brand kit
// ----------------------------------------------------------------------------
function ApplyBrandButton({ applyBrand }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const r = await api.get("/brand-kit");
      const kit = r.data.brand_kit;
      if (!kit) {
        toast.info("Set up your Brand Kit first (Sidebar → Brand Kit)");
        return;
      }
      const changed = applyBrand ? applyBrand(kit) : 0;
      if (changed > 0) {
        toast.success(`Brand applied — updated ${changed} element${changed === 1 ? "" : "s"}`);
      } else {
        toast.info("Already on-brand");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      onClick={onClick}
      disabled={busy}
      className="rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white shadow-sm h-8 px-4 text-xs font-bold"
      data-testid="apply-brand-btn"
      title="Apply your Brand Kit colours, fonts & logo"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
      Brand
    </Button>
  );
}



// --- Text toolbar -----------------------------------------------------------
function TextToolbar({ el, update, designTitle = "", elements = [] }) {
  return (
    <>
      <SmartTextButton el={el} update={update} designTitle={designTitle} elements={elements} />
      <Select value={el.fontFamily || "Outfit"} onValueChange={(v) => update({ fontFamily: v })}>
        <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="font-family"><SelectValue /></SelectTrigger>
        <SelectContent>
          {FONT_FAMILIES.map((f) => (
            <SelectItem key={f.id} value={f.id} style={{ fontFamily: f.css }}>{f.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex items-center bg-slate-100 rounded-md">
        <button onClick={() => update({ fontSize: Math.max(8, (el.fontSize || 32) - 4) })} className="px-1.5 text-slate-600 hover:text-slate-900" data-testid="font-dec"><Minus className="h-3 w-3" /></button>
        <Input
          type="number"
          value={el.fontSize || 32}
          onChange={(e) => update({ fontSize: parseInt(e.target.value, 10) || 16 })}
          className="h-8 w-14 text-xs border-0 bg-transparent text-center focus-visible:ring-0"
          data-testid="font-size"
        />
        <button onClick={() => update({ fontSize: (el.fontSize || 32) + 4 })} className="px-1.5 text-slate-600 hover:text-slate-900" data-testid="font-inc"><Plus className="h-3 w-3" /></button>
      </div>
      <ColorPicker value={el.color || "#000000"} onChange={(c) => update({ color: c })} testid="text-color" />
      <ToolButton title="Bold"      active={el.fontWeight >= 700} onClick={() => update({ fontWeight: (el.fontWeight ?? 400) >= 700 ? 400 : 800 })} testid="text-bold"><Bold className="h-4 w-4" /></ToolButton>
      <ToolButton title="Italic"    active={!!el.italic}     onClick={() => update({ italic: !el.italic })} testid="text-italic"><Italic className="h-4 w-4" /></ToolButton>
      <ToolButton title="Underline" active={!!el.underline}  onClick={() => update({ underline: !el.underline })} testid="text-underline"><Underline className="h-4 w-4" /></ToolButton>
      <ToolButton title="Align left"   active={el.align === "left"}   onClick={() => update({ align: "left" })}  testid="align-left"><AlignLeft className="h-4 w-4" /></ToolButton>
      <ToolButton title="Align center" active={el.align === "center"} onClick={() => update({ align: "center" })} testid="align-center"><AlignCenter className="h-4 w-4" /></ToolButton>
      <ToolButton title="Align right"  active={el.align === "right"}  onClick={() => update({ align: "right" })} testid="align-right"><AlignRight className="h-4 w-4" /></ToolButton>
      <RotationInput el={el} update={update} />
    </>
  );
}

// ----------------------------------------------------------------------------
// Smart text — AI-generated promo copy (Claude Sonnet)
// ----------------------------------------------------------------------------
const SMART_TONES = [
  { id: "catchy",       label: "Catchy" },
  { id: "urgent",       label: "Urgent" },
  { id: "luxurious",    label: "Luxurious" },
  { id: "friendly",     label: "Friendly" },
  { id: "professional", label: "Professional" },
  { id: "playful",      label: "Playful" },
];
const SMART_INTENTS = [
  { id: "headline", label: "Headline" },
  { id: "subtitle", label: "Subtitle" },
  { id: "cta",      label: "Call to action" },
  { id: "body",     label: "Body text" },
];

function SmartTextButton({ el, update, designTitle = "", elements = [] }) {
  const [open, setOpen] = useState(false);
  const [tone, setTone] = useState("catchy");
  const [intent, setIntent] = useState("headline");
  const [industry, setIndustry] = useState("");
  const [loading, setLoading] = useState(false);
  const [variants, setVariants] = useState([]);

  // Pre-fill intent based on the current element's font size (bigger = headline)
  useEffect(() => {
    if (open && el) {
      const fs = el.fontSize || 32;
      setIntent(fs >= 96 ? "headline" : fs >= 56 ? "subtitle" : fs >= 28 ? "body" : "cta");
    }
  }, [open, el]);

  const generate = async () => {
    setLoading(true);
    setVariants([]);
    try {
      // Pull other text on the canvas for context (avoid repetition)
      const existing = (elements || [])
        .filter((e) => e.kind === "text" && e.id !== el.id && e.text)
        .map((e) => e.text);
      const r = await api.post("/ai/text", {
        intent,
        tone,
        count: 3,
        industry: industry || undefined,
        design_title: designTitle || undefined,
        existing,
      });
      setVariants(r.data.variants || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const pick = (txt) => {
    update({ text: txt });
    setOpen(false);
    setVariants([]);
    toast.success("Copy inserted");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="h-8 px-3 text-xs rounded-md border-violet-200 text-violet-700 hover:bg-violet-50 bg-violet-50/40"
          data-testid="smart-text-btn"
          title="Generate with AI"
        >
          <Wand2 className="h-3.5 w-3.5 mr-1.5" /> AI write
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-4 bg-white border border-slate-200" align="start" data-testid="smart-text-popover">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-violet-600 mb-3">Generate copy</div>

        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Type</label>
            <Select value={intent} onValueChange={setIntent}>
              <SelectTrigger className="h-8 text-xs" data-testid="smart-text-intent"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SMART_INTENTS.map((i) => (
                  <SelectItem key={i.id} value={i.id}>{i.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Tone</label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger className="h-8 text-xs" data-testid="smart-text-tone"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SMART_TONES.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Business / industry <span className="opacity-60 normal-case">(optional)</span></label>
        <Input
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
          placeholder="e.g. Yoga studio, Coffee shop, Dentist"
          className="h-8 text-xs mb-3"
          data-testid="smart-text-industry"
        />

        <Button
          onClick={generate}
          disabled={loading}
          className="w-full h-9 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold"
          data-testid="smart-text-generate"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
          {loading ? "Thinking…" : "Generate 3 ideas"}
        </Button>

        {variants.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="smart-text-variants">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Click one to use</div>
            {variants.map((v, i) => (
              <button
                key={i}
                onClick={() => pick(v)}
                className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-violet-300 hover:bg-violet-50 text-sm text-slate-800 transition"
                data-testid={`smart-text-variant-${i}`}
              >
                {v}
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}



// Every shape we support. Grouped so the picker reads geometry → arrows → decorative.
const SHAPE_OPTIONS = [
  // Basic geometry
  { value: "rect",         label: "Rectangle" },
  { value: "rounded-rect", label: "Rounded rect" },
  { value: "circle",       label: "Circle" },
  { value: "triangle",     label: "Triangle" },
  { value: "right-triangle", label: "Right triangle" },
  { value: "diamond",      label: "Diamond" },
  { value: "kite",         label: "Kite" },
  { value: "parallelogram",label: "Parallelogram" },
  { value: "trapezoid",    label: "Trapezoid" },
  // Polygons
  { value: "pentagon",     label: "Pentagon" },
  { value: "hexagon",      label: "Hexagon (horiz.)" },
  { value: "hexagon-v",    label: "Hexagon (vert.)" },
  { value: "octagon",      label: "Octagon" },
  { value: "semicircle-top",    label: "Semicircle (top)" },
  { value: "semicircle-bottom", label: "Semicircle (bottom)" },
  { value: "quarter-circle",    label: "Quarter circle" },
  // Arrows & chevrons
  { value: "arrow-right",  label: "Arrow →" },
  { value: "arrow-left",   label: "Arrow ←" },
  { value: "arrow-up",     label: "Arrow ↑" },
  { value: "arrow-down",   label: "Arrow ↓" },
  { value: "chevron-right",label: "Chevron ►" },
  { value: "chevron-left", label: "Chevron ◄" },
  { value: "chevron-up",   label: "Chevron ▲" },
  { value: "chevron-down", label: "Chevron ▼" },
  // Stars & bursts
  { value: "star",         label: "Star" },
  { value: "star4",        label: "4-point star" },
  { value: "star6",        label: "6-point star" },
  { value: "star8",        label: "8-point star" },
  { value: "burst",        label: "Burst / sunburst" },
  { value: "bolt",         label: "Bolt" },
  { value: "plus",         label: "Plus / cross" },
  { value: "cross-x",      label: "Cross (×)" },
  // Decorative
  { value: "heart",        label: "Heart" },
  { value: "speech",       label: "Speech bubble" },
  { value: "cloud",        label: "Cloud" },
  { value: "flag",         label: "Flag" },
  { value: "bookmark",     label: "Bookmark" },
  { value: "tag",          label: "Tag" },
];

function ShapeToolbar({ el, update }) {
  return (
    <>
      <Select value={el.shape || "rect"} onValueChange={(v) => update({ shape: v })}>
        <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="shape-picker"><SelectValue /></SelectTrigger>
        <SelectContent>
          {SHAPE_OPTIONS.map((s) => (
            <SelectItem key={s.value} value={s.value} data-testid={`shape-option-${s.value}`}>
              {s.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <ColorPicker value={el.fill || "#0EA5E9"} onChange={(c) => update({ fill: c })} testid="shape-fill" />
      <RotationInput el={el} update={update} />
      <div className="flex items-center gap-1 text-xs text-slate-600">
        <span className="text-slate-400">Opacity</span>
        <Input type="number" min="0" max="100" value={Math.round((el.opacity ?? 1) * 100)}
               onChange={(e) => update({ opacity: Math.min(1, Math.max(0, parseInt(e.target.value, 10) / 100)) })}
               className="h-8 w-16 text-xs" />
      </div>
      {(el.shape === "rect" || !el.shape) && (
        <div className="flex items-center gap-1 text-xs text-slate-600">
          <span className="text-slate-400">Radius</span>
          <Input type="number" min="0" value={el.borderRadius || 0}
                 onChange={(e) => update({ borderRadius: parseInt(e.target.value, 10) || 0 })}
                 className="h-8 w-16 text-xs" />
        </div>
      )}
    </>
  );
}

function ImageToolbar({ el, update, onReplaceImage }) {
  const [removingBg, setRemovingBg] = useState(false);
  // Extract file_id from src URLs of the form /api/files/{id}
  const fileIdMatch = (el.src || "").match(/\/api\/files\/([0-9a-f-]{36})/i);
  const fileId = fileIdMatch?.[1];
  const isRemoteUrl = !!el.src && !fileId && /^https?:\/\//i.test(el.src);

  const handleRemoveBg = async () => {
    if (!fileId && !isRemoteUrl) {
      toast.error("This image doesn't have a source URL yet. Use 'Change image' first.");
      return;
    }
    setRemovingBg(true);
    const tId = toast.loading("Removing background… (≈10s)");
    try {
      const body = fileId ? { file_id: fileId } : { url: el.src };
      const r = await api.post("/images/remove-bg", body);
      update({ src: r.data.url });
      toast.success("Background removed", { id: tId });
    } catch (err) {
      toast.error(formatApiError(err), { id: tId });
    } finally {
      setRemovingBg(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={onReplaceImage}
        className="h-8 px-3 text-xs rounded-md border-sky-200 text-sky-700 hover:bg-sky-50"
        data-testid="image-replace-btn"
      >
        <ImageIcon className="h-3.5 w-3.5 mr-1.5" /> Change image
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={handleRemoveBg}
        disabled={removingBg || (!fileId && !isRemoteUrl)}
        className="h-8 px-3 text-xs rounded-md border-violet-200 text-violet-700 hover:bg-violet-50 disabled:opacity-50"
        data-testid="image-removebg-btn"
        title="Remove background with AI"
      >
        {removingBg
          ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          : <Wand2 className="h-3.5 w-3.5 mr-1.5" />}
        {removingBg ? "Removing…" : "Remove BG"}
      </Button>
      <MagicEditButton fileId={fileId} onResult={(url) => update({ src: url })} />
      <Select value={el.fit || "cover"} onValueChange={(v) => update({ fit: v })}>
        <SelectTrigger className="h-8 w-[110px] text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="cover">Cover</SelectItem>
          <SelectItem value="contain">Contain</SelectItem>
          <SelectItem value="fill">Fill</SelectItem>
        </SelectContent>
      </Select>
      <RotationInput el={el} update={update} />
      <div className="flex items-center gap-1 text-xs text-slate-600">
        <span className="text-slate-400">Opacity</span>
        <Input type="number" min="0" max="100" value={Math.round((el.opacity ?? 1) * 100)}
               onChange={(e) => update({ opacity: Math.min(1, Math.max(0, parseInt(e.target.value, 10) / 100)) })}
               className="h-8 w-16 text-xs" />
      </div>
      <div className="flex items-center gap-1 text-xs text-slate-600">
        <span className="text-slate-400">Round</span>
        <Input type="number" min="0" value={el.borderRadius || 0}
               onChange={(e) => update({ borderRadius: parseInt(e.target.value, 10) || 0 })}
               className="h-8 w-16 text-xs" />
      </div>
    </>
  );
}

// ─── Video element toolbar ──────────────────────────────────────────────────
// Controls for the audio/loop/fit/opacity behaviour of a <video> element.
// Kept intentionally separate from ImageToolbar because BG-removal and
// Magic-Edit don't apply to video files, and video needs muted/loop toggles
// that images obviously don't.
function VideoToolbar({ el, update, onReplaceImage }) {
  // Default to muted=true (matches the autoplay-safe default across
  // browsers + Android WebView). The customer can toggle sound ON per-
  // video from this toolbar. Muted-off videos will still autoplay because
  // the Android launcher sets setMediaPlaybackRequiresUserGesture(false).
  const muted = el.muted !== false;
  const looping = el.loop !== false;
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={onReplaceImage}
        className="h-8 px-3 text-xs rounded-md border-rose-200 text-rose-700 hover:bg-rose-50"
        title="Swap this video for a different file"
        data-testid="video-replace-btn"
      >
        <Film className="h-3.5 w-3.5 mr-1.5" /> Change video
      </Button>
      <Button
        size="sm"
        variant={muted ? "outline" : "default"}
        onClick={() => update({ muted: !muted })}
        className={muted
          ? "h-8 px-3 text-xs rounded-md border-slate-300 text-slate-600 hover:bg-slate-50"
          : "h-8 px-3 text-xs rounded-md bg-emerald-500 hover:bg-emerald-600 text-white"}
        title={muted ? "Turn ON sound (video will play with audio on the TV)" : "Mute — video will play silently"}
        data-testid="video-sound-toggle"
      >
        {muted
          ? <><VolumeX className="h-3.5 w-3.5 mr-1.5" /> Muted</>
          : <><Volume2 className="h-3.5 w-3.5 mr-1.5" /> Sound on</>}
      </Button>
      <Button
        size="sm"
        variant={looping ? "default" : "outline"}
        onClick={() => update({ loop: !looping })}
        className={looping
          ? "h-8 px-3 text-xs rounded-md bg-sky-500 hover:bg-sky-600 text-white"
          : "h-8 px-3 text-xs rounded-md border-slate-300 text-slate-600 hover:bg-slate-50"}
        title={looping ? "Video restarts when it ends" : "Video plays once then stops on the last frame"}
        data-testid="video-loop-toggle"
      >
        <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
        {looping ? "Loop" : "Play once"}
      </Button>
      <Select value={el.fit || "contain"} onValueChange={(v) => update({ fit: v })}>
        <SelectTrigger className="h-8 w-[110px] text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="cover">Cover</SelectItem>
          <SelectItem value="contain">Contain</SelectItem>
          <SelectItem value="fill">Fill</SelectItem>
        </SelectContent>
      </Select>
      <RotationInput el={el} update={update} />
      <div className="flex items-center gap-1 text-xs text-slate-600">
        <span className="text-slate-400">Opacity</span>
        <Input type="number" min="0" max="100" value={Math.round((el.opacity ?? 1) * 100)}
               onChange={(e) => update({ opacity: Math.min(1, Math.max(0, parseInt(e.target.value, 10) / 100)) })}
               className="h-8 w-16 text-xs" />
      </div>
    </>
  );
}

// Compact rotation input reused by image/shape/text toolbars.
function RotationInput({ el, update }) {
  const rot = Number.isFinite(el.rotation) ? el.rotation : 0;
  return (
    <div className="flex items-center gap-1 text-xs text-slate-600" data-testid="rotation-input">
      <span className="text-slate-400">Rotate</span>
      <Input
        type="number"
        min="-180"
        max="180"
        step="1"
        value={rot}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          update({ rotation: Number.isFinite(n) ? ((n % 360 + 540) % 360) - 180 : 0 });
        }}
        className="h-8 w-16 text-xs"
        data-testid="rotation-input-field"
      />
      <span className="text-slate-400">°</span>
      <button
        type="button"
        onClick={() => update({ rotation: (((rot + 90) % 360 + 540) % 360) - 180 })}
        className="h-7 w-7 rounded-md border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center text-slate-500"
        title="Rotate 90°"
        data-testid="rotation-quarter-btn"
      >
        <RotateCw className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Magic Edit — Nano Banana image editing on an existing canvas image
// ----------------------------------------------------------------------------
const MAGIC_EDIT_SUGGESTIONS = [
  "Make the background a sunset beach",
  "Add soft fog and warm cinematic lighting",
  "Replace the background with a luxury marble studio",
  "Make the colors more vibrant and high-contrast",
  "Add a soft drop shadow under the subject",
  "Change the background to a clean white studio",
];

function MagicEditButton({ fileId, onResult }) {
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const v = (instruction || "").trim();
    if (!v) {
      toast.error("Describe what to change");
      return;
    }
    if (!fileId) {
      toast.error("Magic Edit works on uploaded images. Use 'Change image' first.");
      return;
    }
    setBusy(true);
    const tId = toast.loading("Magic editing… (≈10s)");
    try {
      const r = await api.post("/ai/image/edit", { file_id: fileId, instruction: v });
      onResult(r.data.url);
      toast.success("Edited & saved to library", { id: tId });
      setOpen(false);
      setInstruction("");
    } catch (err) {
      toast.error(formatApiError(err), { id: tId });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          disabled={!fileId}
          className="h-8 px-3 text-xs rounded-md border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50 disabled:opacity-50"
          data-testid="image-magicedit-btn"
          title={fileId ? "Edit with AI — describe what to change" : "Available for uploaded images only"}
        >
          <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Magic edit
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-4 bg-white border border-slate-200" align="start" data-testid="magic-edit-popover">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-fuchsia-600 mb-3">Magic edit</div>
        <textarea
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
          rows={3}
          placeholder="e.g. Make the background a sunset beach"
          className="w-full rounded-lg border border-slate-200 p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-200 resize-none"
          data-testid="magic-edit-instruction"
          disabled={busy}
        />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {MAGIC_EDIT_SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => setInstruction(s)}
              className="text-[11px] px-2 py-1 rounded-full bg-slate-50 border border-slate-200 hover:border-fuchsia-300 hover:bg-fuchsia-50 text-slate-600"
              disabled={busy}
            >
              {s.length > 30 ? s.slice(0, 28) + "…" : s}
            </button>
          ))}
        </div>
        <Button
          onClick={submit}
          disabled={busy || !instruction.trim()}
          className="mt-4 w-full h-9 rounded-md bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs font-bold"
          data-testid="magic-edit-submit"
        >
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
          {busy ? "Editing…" : "Apply edit"}
        </Button>
        <div className="mt-2 text-[11px] text-slate-400">
          The edited image is saved as a new asset. Your original stays untouched.
        </div>
      </PopoverContent>
    </Popover>
  );
}

function WidgetToolbar({ el, update }) {
  return (
    <>
      <Select value={el.widgetType || "weather"} onValueChange={(v) => update({ widgetType: v, widgetConfig: defaultWidgetConfig(v) })}>
        <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="widget-type"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="weather">Weather</SelectItem>
          <SelectItem value="clock">Clock</SelectItem>
          <SelectItem value="qr">QR Code</SelectItem>
          <SelectItem value="countdown">Countdown</SelectItem>
          <SelectItem value="ticker">Ticker</SelectItem>
        </SelectContent>
      </Select>
      <Popover>
        <PopoverTrigger asChild>
          <button
            className="h-8 px-3 inline-flex items-center gap-1.5 rounded-md bg-sky-50 hover:bg-sky-100 text-sky-700 text-xs font-semibold border border-sky-200"
            data-testid="widget-settings-btn"
          >
            <SettingsIcon className="h-3.5 w-3.5" /> Edit content & style
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[360px] p-4 bg-white border border-slate-200 max-h-[70vh] overflow-y-auto" align="start" sideOffset={8}>
          <WidgetSettings
            type={el.widgetType}
            config={el.widgetConfig || {}}
            onChange={(c) => update({ widgetConfig: c })}
          />
        </PopoverContent>
      </Popover>
    </>
  );
}

// --- Color picker -----------------------------------------------------------
function ColorPicker({ value, onChange, testid }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="h-8 w-8 rounded-md border border-slate-300 flex items-center justify-center"
          data-testid={testid}
          title="Color"
        >
          <div className="h-4 w-4 rounded" style={{ background: value }} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3 bg-white border border-slate-200" align="start">
        <div className="grid grid-cols-6 gap-2 mb-3">
          {PRESET_COLORS.map((c) => (
            <button key={c} onClick={() => onChange(c)} className="h-8 w-8 rounded-md border border-slate-200" style={{ background: c }} title={c} />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 border-0" />
          <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-9 text-xs" />
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ============================================================================
// Per-element hit box: selection ring + 8 resize handles + drag area
// ============================================================================
function ElementHitBox({
  el, selected, editing, onMouseDown, onResizeMouseDown,
  onDoubleClick, onTextEdit, onTextEditDone, scale,
}) {
  const ringColor = "#0EA5E9";
  // Larger handles on mobile so fingertips can grab them. We compute this
  // once per render from the coarse-pointer media query.
  const isCoarsePointer = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
  const handleSize = (isCoarsePointer ? 20 : 12) / scale;
  const handleStyle = {
    position: "absolute",
    width: handleSize,
    height: handleSize,
    background: "#FFFFFF",
    border: `${2 / scale}px solid ${ringColor}`,
    borderRadius: "50%",
    boxShadow: `0 ${2/scale}px ${6/scale}px rgba(15,23,42,0.18)`,
    pointerEvents: "auto",
    touchAction: "none",
    zIndex: 50,
  };
  const handles = [
    { id: "nw", cursor: "nwse-resize", style: { left: -handleSize/2, top: -handleSize/2 } },
    { id: "n",  cursor: "ns-resize",   style: { left: el.w/2 - handleSize/2, top: -handleSize/2 } },
    { id: "ne", cursor: "nesw-resize", style: { right: -handleSize/2, top: -handleSize/2 } },
    { id: "e",  cursor: "ew-resize",   style: { right: -handleSize/2, top: el.h/2 - handleSize/2 } },
    { id: "se", cursor: "nwse-resize", style: { right: -handleSize/2, bottom: -handleSize/2 } },
    { id: "s",  cursor: "ns-resize",   style: { right: el.w/2 - handleSize/2, bottom: -handleSize/2 } },
    { id: "sw", cursor: "nesw-resize", style: { left: -handleSize/2, bottom: -handleSize/2 } },
    { id: "w",  cursor: "ew-resize",   style: { left: -handleSize/2, top: el.h/2 - handleSize/2 } },
  ];
  return (
    <div
      data-testid={`canvas-el-${el.id}`}
      className="absolute"
      style={{
        left: el.x,
        top: el.y,
        width: el.w,
        height: el.h,
        transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
        transformOrigin: "center center",
        cursor: el.locked ? "default" : (editing ? "text" : "move"),
        pointerEvents: el.locked && !selected ? "none" : "auto",
        // Block browser touch gestures (scroll, pinch-zoom) on this element so
        // drag-to-move works cleanly on mobile.
        touchAction: "none",
      }}
      onPointerDown={onMouseDown}
      onDoubleClick={onDoubleClick}
    >
      {/* Edit-in-place for text */}
      {el.kind === "text" && editing && (
        <textarea
          autoFocus
          value={el.text || ""}
          onChange={(e) => onTextEdit(e.target.value)}
          onBlur={onTextEditDone}
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%", height: "100%",
            background: "rgba(14,165,233,0.05)",
            border: `${2/scale}px dashed ${ringColor}`,
            color: el.color,
            fontFamily: fontCss(el.fontFamily),
            fontSize: el.fontSize,
            fontWeight: el.fontWeight ?? 400,
            fontStyle: el.italic ? "italic" : "normal",
            textDecoration: el.underline ? "underline" : "none",
            textAlign: el.align || "left",
            lineHeight: el.lineHeight ?? 1.2,
            letterSpacing: el.letterSpacing ?? 0,
            outline: "none",
            resize: "none",
            padding: 0,
          }}
        />
      )}

      {/* Selection ring + handles */}
      {selected && !editing && (
        <>
          <div
            aria-hidden="true"
            className="absolute pointer-events-none"
            style={{
              inset: -1 / scale,
              border: `${2 / scale}px solid ${ringColor}`,
              borderRadius: 2 / scale,
            }}
          />
          {!el.locked && handles.map((h) => (
            <div
              key={h.id}
              style={{ ...handleStyle, ...h.style, cursor: h.cursor }}
              onPointerDown={(e) => onResizeMouseDown(e, h.id)}
            />
          ))}
        </>
      )}
    </div>
  );
}


// ============================================================================
// QuickActionPill — small floating action bar (Delete · Rotate · Lock · Copy)
// that sits just below the selected element's bounding box. Rendered in the
// scaled interactive layer (canvas coords) but the pill itself is inversely
// scaled so its icons/text stay at a constant size regardless of zoom, and
// counter-rotation is never applied — the pill's position uses the
// pre-rotation bounding box so it stays upright and always readable.
// ============================================================================
function QuickActionPill({ el, scale, canvasH, stageRef, onDelete, onDuplicate, onLockToggle, onRotate }) {
  const [dragging, setDragging] = useState(false);
  const [liveAngle, setLiveAngle] = useState(null); // shown near cursor while dragging
  const rot = Number.isFinite(el.rotation) ? el.rotation : 0;

  // Position in canvas coords: center-x of element, just below its box.
  // If the element is close to the bottom, flip the pill above instead.
  const centerX = (el.x ?? 0) + (el.w ?? 0) / 2;
  const gapCanvas = 14 / scale;
  const wouldOverflow = (el.y ?? 0) + (el.h ?? 0) + gapCanvas + 60 / scale > canvasH;
  const topCanvas = wouldOverflow
    ? (el.y ?? 0) - gapCanvas - 44 / scale
    : (el.y ?? 0) + (el.h ?? 0) + gapCanvas;

  // ---------------------------------------------------------------------------
  // Drag-to-rotate — Canva-style. Works on desktop mouse AND mobile touch
  // via the unified PointerEvents API. On pointerdown we lock the element
  // center in screen coords + the current rotation offset, then on each
  // pointermove we translate the cursor angle around that center back into
  // an element rotation. Shift-drag snaps to 15° increments.
  // ---------------------------------------------------------------------------
  const beginRotate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!stageRef?.current) return;
    const stageRect = stageRef.current.getBoundingClientRect();
    // Element visual center in screen space (rotation is around this point).
    const cx = stageRect.left + ((el.x ?? 0) + (el.w ?? 0) / 2) * scale;
    const cy = stageRect.top  + ((el.y ?? 0) + (el.h ?? 0) / 2) * scale;
    const startCursorAngle = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI);
    const startRotation = rot;
    setDragging(true);
    // Capture the pointer so subsequent moves fire on this button even if
    // the finger drifts far away from it.
    if (e.pointerId != null && e.currentTarget?.setPointerCapture) {
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ok */ }
    }

    const move = (ev) => {
      const currentAngle = Math.atan2(ev.clientY - cy, ev.clientX - cx) * (180 / Math.PI);
      let next = startRotation + (currentAngle - startCursorAngle);
      // Shift → snap to 15° increments so users can lock nice angles.
      if (ev.shiftKey) next = Math.round(next / 15) * 15;
      // Normalize to -180…180 for a clean number.
      next = ((next % 360 + 540) % 360) - 180;
      setLiveAngle(next);
      onRotate(next);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup",   up);
      window.removeEventListener("pointercancel", up);
      setDragging(false);
      setLiveAngle(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup",   up);
    window.addEventListener("pointercancel", up);
  };

  return (
    <div
      className="absolute"
      style={{
        left: centerX,
        top: topCanvas,
        transform: `translateX(-50%) scale(${1 / scale})`,
        transformOrigin: "top center",
        pointerEvents: "auto",
      }}
      onPointerDown={(e) => e.stopPropagation() /* don't deselect */}
      data-testid="quick-action-pill"
    >
      <div className="bg-white border border-slate-200 rounded-full shadow-lg flex items-center gap-0.5 px-1.5 py-1">
        {/* DELETE */}
        <button
          onClick={onDelete}
          className="h-8 w-8 rounded-full hover:bg-rose-50 flex items-center justify-center text-rose-500 transition-colors"
          title="Delete"
          data-testid="pill-delete"
        >
          <Trash2 className="h-4 w-4" />
        </button>
        {/* ROTATE — click and drag around the element to rotate. Shift-drag = 15° snap */}
        <button
          onPointerDown={beginRotate}
          onDoubleClick={(e) => { e.stopPropagation(); onRotate(0); }}
          className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${
            dragging ? "bg-sky-100 text-sky-700" : "hover:bg-slate-100 text-slate-600"
          }`}
          style={{ cursor: "grab", touchAction: "none" }}
          title="Drag to rotate · Shift-drag to snap · Double-click to reset"
          data-testid="pill-rotate"
        >
          <RotateCw className="h-4 w-4" />
        </button>
        {/* LOCK */}
        <button
          onClick={onLockToggle}
          className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600 transition-colors"
          title={el.locked ? "Unlock" : "Lock"}
          data-testid="pill-lock"
        >
          {el.locked
            ? <Lock className="h-4 w-4 text-rose-500" />
            : <Unlock className="h-4 w-4" />}
        </button>
        {/* COPY */}
        <button
          onClick={onDuplicate}
          className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600 transition-colors"
          title="Duplicate"
          data-testid="pill-duplicate"
        >
          <Copy className="h-4 w-4" />
        </button>
      </div>

      {/* Live angle chip — appears next to the pill while dragging so users
          can see the exact angle they're setting. */}
      {dragging && (
        <div
          className="absolute left-1/2 -translate-x-1/2 mt-2 px-2.5 py-1 rounded-full bg-sky-500 text-white text-xs font-bold shadow-lg tabular-nums pointer-events-none select-none"
          style={{ top: "100%" }}
          data-testid="pill-rotate-live-angle"
        >
          {Math.round(liveAngle ?? rot)}°
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SnapGuides — the pink alignment lines that flash during a drag.
// Rendered inside the scaled interactive layer in canvas coords. Vertical
// guides (axis: 'v') stretch full canvas height at `pos` on the x-axis;
// horizontal guides (axis: 'h') stretch full canvas width at `pos` on the
// y-axis. Extra "size" guides show a little pill label next to a matched
// element to indicate an equal-width or equal-height alignment.
// ============================================================================
function SnapGuides({ guides, canvasW, canvasH, scale }) {
  if (!guides || guides.length === 0) return null;
  const strokeW = Math.max(1, 2 / scale); // stay 2px on screen regardless of zoom
  const px = (n) => `${n}px`;
  const color = "#EC4899"; // Canva-style pink/magenta
  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="snap-guides">
      {guides.map((g, i) => {
        if (g.axis === "v") {
          return (
            <div
              key={`v-${i}-${g.kind}`}
              data-testid={`snap-guide-v-${g.kind}`}
              className="absolute"
              style={{
                left: g.pos,
                top: 0,
                width: 0,
                height: canvasH,
                borderLeft: `${strokeW}px dashed ${color}`,
              }}
            />
          );
        }
        if (g.axis === "h") {
          return (
            <div
              key={`h-${i}-${g.kind}`}
              data-testid={`snap-guide-h-${g.kind}`}
              className="absolute"
              style={{
                left: 0,
                top: g.pos,
                width: canvasW,
                height: 0,
                borderTop: `${strokeW}px dashed ${color}`,
              }}
            />
          );
        }
        if (g.axis === "size" && g.ref) {
          // Show a little pill on the equal-sized element so the user knows
          // *why* the dragged element locked to that size.
          const el = g.ref;
          const cx = el.x + el.w / 2;
          const cy = el.y + el.h / 2;
          return (
            <div
              key={`s-${i}-${g.kind}-${el.id}`}
              data-testid={`snap-guide-size-${g.kind}`}
              className="absolute"
              style={{
                left: cx,
                top: cy,
                transform: `translate(-50%, -50%) scale(${1 / scale})`,
                transformOrigin: "center",
                background: color,
                color: "white",
                padding: "2px 8px",
                borderRadius: "9999px",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              {g.kind === "same-w" ? "SAME W" : "SAME H"}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}


// ============================================================================
// AddShapePicker — the "Add shape" toolbar button. Instead of always adding
// a rectangle, clicking it opens a 4x4 grid picker of 16 shapes, and clicking
// one adds that shape to the canvas.
// ============================================================================
function AddShapePicker({ addElement }) {
  const [open, setOpen] = useState(false);
  // 32 shapes organized by group so the picker reads geometry → arrows → stars → decorative.
  const shapes = [
    // Basic geometry (row 1)
    { v: "rect",           label: "Rect" },
    { v: "rounded-rect",   label: "Rounded" },
    { v: "circle",         label: "Circle" },
    { v: "triangle",       label: "Triangle" },
    { v: "right-triangle", label: "R-Triangle" },
    { v: "diamond",        label: "Diamond" },
    { v: "kite",           label: "Kite" },
    { v: "parallelogram",  label: "Parallelo." },
    // Polygons (row 2)
    { v: "trapezoid",      label: "Trapezoid" },
    { v: "pentagon",       label: "Pentagon" },
    { v: "hexagon",        label: "Hexagon" },
    { v: "hexagon-v",      label: "Hex vert." },
    { v: "octagon",        label: "Octagon" },
    { v: "semicircle-top", label: "Semi-top" },
    { v: "semicircle-bottom", label: "Semi-bot" },
    { v: "quarter-circle", label: "Quarter" },
    // Arrows & chevrons (row 3)
    { v: "arrow-left",     label: "← Arrow" },
    { v: "arrow-right",    label: "→ Arrow" },
    { v: "arrow-up",       label: "↑ Arrow" },
    { v: "arrow-down",     label: "↓ Arrow" },
    { v: "chevron-left",   label: "◄ Chevron" },
    { v: "chevron-right",  label: "► Chevron" },
    { v: "chevron-up",     label: "▲ Chevron" },
    { v: "chevron-down",   label: "▼ Chevron" },
    // Stars & bursts (row 4)
    { v: "star",           label: "Star" },
    { v: "star4",          label: "4-star" },
    { v: "star6",          label: "6-star" },
    { v: "star8",          label: "8-star" },
    { v: "burst",          label: "Burst" },
    { v: "bolt",           label: "Bolt" },
    { v: "plus",           label: "Plus" },
    { v: "cross-x",        label: "Cross X" },
    // Decorative (row 5)
    { v: "heart",          label: "Heart" },
    { v: "speech",         label: "Speech" },
    { v: "cloud",          label: "Cloud" },
    { v: "flag",           label: "Flag" },
    { v: "bookmark",       label: "Bookmark" },
    { v: "tag",            label: "Tag" },
  ];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          title="Add shape"
          className="h-8 w-8 rounded-md inline-flex items-center justify-center transition-colors text-slate-700 hover:bg-slate-100"
          data-testid="add-shape"
        >
          <Square className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-3 bg-white border border-slate-200 max-h-[70vh] overflow-y-auto" align="start">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">
          Add shape <span className="text-slate-400 font-normal normal-case tracking-normal ml-1">· {shapes.length} shapes</span>
        </div>
        <div className="grid grid-cols-6 gap-1.5">
          {shapes.map((s) => (
            <button
              key={s.v}
              onClick={() => {
                addElement("shape", { shape: s.v, w: 320, h: 320, fill: "#0EA5E9" });
                setOpen(false);
              }}
              className="group flex flex-col items-center gap-1 p-1.5 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all"
              title={s.label}
              data-testid={`add-shape-${s.v}`}
            >
              <div className="h-9 w-9 flex items-center justify-center">
                <ShapeThumb shape={s.v} />
              </div>
              <div className="text-[9px] text-slate-500 group-hover:text-slate-700 truncate max-w-full leading-none">{s.label}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Tiny visual preview inside the shape picker so users can see the shape.
function ShapeThumb({ shape }) {
  const size = 28;
  const fill = "#0EA5E9";
  const style = { width: size, height: size, background: fill };
  const clip = {
    triangle:      "polygon(50% 0%, 100% 100%, 0% 100%)",
    "right-triangle": "polygon(0% 0%, 0% 100%, 100% 100%)",
    diamond:       "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
    hexagon:       "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
    "hexagon-v":   "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
    pentagon:      "polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)",
    octagon:       "polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)",
    star:          "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)",
    star4:         "polygon(50% 0%, 60% 40%, 100% 50%, 60% 60%, 50% 100%, 40% 60%, 0% 50%, 40% 40%)",
    star6:         "polygon(50% 0%, 62% 34%, 100% 34%, 68% 55%, 82% 100%, 50% 74%, 18% 100%, 32% 55%, 0% 34%, 38% 34%)",
    star8:         "polygon(50% 0%, 58% 20%, 79% 15%, 76% 36%, 100% 50%, 76% 64%, 79% 85%, 58% 80%, 50% 100%, 42% 80%, 21% 85%, 24% 64%, 0% 50%, 24% 36%, 21% 15%, 42% 20%)",
    burst:         "polygon(50% 0%, 58% 15%, 75% 5%, 75% 25%, 95% 25%, 82% 40%, 100% 50%, 82% 60%, 95% 75%, 75% 75%, 75% 95%, 58% 85%, 50% 100%, 42% 85%, 25% 95%, 25% 75%, 5% 75%, 18% 60%, 0% 50%, 18% 40%, 5% 25%, 25% 25%, 25% 5%, 42% 15%)",
    "arrow-right": "polygon(0% 25%, 60% 25%, 60% 0%, 100% 50%, 60% 100%, 60% 75%, 0% 75%)",
    "arrow-left":  "polygon(40% 0%, 40% 25%, 100% 25%, 100% 75%, 40% 75%, 40% 100%, 0% 50%)",
    "arrow-up":    "polygon(50% 0%, 100% 40%, 75% 40%, 75% 100%, 25% 100%, 25% 40%, 0% 40%)",
    "arrow-down":  "polygon(25% 0%, 75% 0%, 75% 60%, 100% 60%, 50% 100%, 0% 60%, 25% 60%)",
    "chevron-right":"polygon(0% 0%, 65% 0%, 100% 50%, 65% 100%, 0% 100%, 35% 50%)",
    "chevron-left":"polygon(35% 0%, 100% 0%, 65% 50%, 100% 100%, 35% 100%, 0% 50%)",
    "chevron-up":  "polygon(50% 0%, 100% 35%, 100% 65%, 50% 30%, 0% 65%, 0% 35%)",
    "chevron-down":"polygon(0% 35%, 0% 65%, 50% 100%, 100% 65%, 100% 35%, 50% 70%)",
    plus:          "polygon(35% 0%, 65% 0%, 65% 35%, 100% 35%, 100% 65%, 65% 65%, 65% 100%, 35% 100%, 35% 65%, 0% 65%, 0% 35%, 35% 35%)",
    "cross-x":     "polygon(0% 15%, 15% 0%, 50% 35%, 85% 0%, 100% 15%, 65% 50%, 100% 85%, 85% 100%, 50% 65%, 15% 100%, 0% 85%, 35% 50%)",
    parallelogram: "polygon(20% 0%, 100% 0%, 80% 100%, 0% 100%)",
    trapezoid:     "polygon(20% 0%, 80% 0%, 100% 100%, 0% 100%)",
    bolt:          "polygon(52% 0%, 15% 55%, 42% 55%, 30% 100%, 85% 40%, 55% 40%, 68% 0%)",
    flag:          "polygon(0% 0%, 100% 0%, 85% 50%, 100% 100%, 0% 100%)",
    bookmark:      "polygon(0% 0%, 100% 0%, 100% 100%, 50% 80%, 0% 100%)",
    tag:           "polygon(0% 50%, 20% 0%, 100% 0%, 100% 100%, 20% 100%)",
    kite:          "polygon(50% 0%, 100% 35%, 50% 100%, 0% 35%)",
  }[shape];
  if (shape === "circle") return <div style={{ ...style, borderRadius: "50%" }} />;
  if (shape === "rect") return <div style={{ ...style, borderRadius: 3 }} />;
  if (shape === "rounded-rect") return <div style={{ ...style, borderRadius: 8 }} />;
  if (shape === "heart") {
    return (
      <svg width={size} height={size} viewBox="0 0 100 100"><path d="M50 88 C 20 68, 5 45, 15 25 C 22 12, 40 12, 50 28 C 60 12, 78 12, 85 25 C 95 45, 80 68, 50 88 Z" fill={fill} /></svg>
    );
  }
  if (shape === "speech") {
    return (
      <svg width={size} height={size} viewBox="0 0 100 100"><path d="M8 8 H92 A8 8 0 0 1 100 16 V70 A8 8 0 0 1 92 78 H40 L22 96 L28 78 H8 A8 8 0 0 1 0 70 V16 A8 8 0 0 1 8 8 Z" fill={fill} /></svg>
    );
  }
  if (shape === "cloud") {
    return (
      <svg width={size} height={size} viewBox="0 0 120 60" preserveAspectRatio="xMidYMid meet"><path d="M30 55 C 12 55, 5 40, 15 30 C 15 15, 35 8, 45 18 C 50 5, 75 5, 82 20 C 105 15, 120 35, 105 50 Z" fill={fill} /></svg>
    );
  }
  if (shape === "semicircle-top") {
    return (<svg width={size} height={size} viewBox="0 0 100 100"><path d="M 0 100 A 50 100 0 0 1 100 100 Z" fill={fill} /></svg>);
  }
  if (shape === "semicircle-bottom") {
    return (<svg width={size} height={size} viewBox="0 0 100 100"><path d="M 0 0 A 50 100 0 0 0 100 0 Z" fill={fill} /></svg>);
  }
  if (shape === "quarter-circle") {
    return (<svg width={size} height={size} viewBox="0 0 100 100"><path d="M 0 100 L 0 0 A 100 100 0 0 1 100 100 Z" fill={fill} /></svg>);
  }
  return <div style={{ ...style, clipPath: clip, WebkitClipPath: clip }} />;
}

// ============================================================================
// LayersPanel — right slide-out that lists every element in z-order (top-most
// first, like Photoshop/Canva). Users can:
//   • Click a row to select the element on the canvas
//   • Drag rows up/down to reorder z
//   • Toggle a lock icon per row
//   • Rename via double-click on the label
//   • Duplicate / delete via the row's action buttons
// ============================================================================
function LayersPanel({ elements, selectedId, onSelect, onClose, onReorder, onToggleLock, onDelete, onDuplicate, onRename }) {
  // Sort by z DESC (top-most first) — display top-down.
  const rows = [...elements].sort((a, b) => (b.z ?? 0) - (a.z ?? 0));
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [renaming, setRenaming] = useState(null);

  const humanKind = (el) => (
    el.layerName?.trim()
      ? el.layerName
      : el.kind === "text"
        ? (el.text?.split("\n")[0] || "Text").slice(0, 40)
        : el.kind === "image"
          ? "Image"
          : el.kind === "shape"
            ? `Shape · ${el.shape || "rect"}`
            : el.kind === "widget"
              ? `Widget · ${el.widgetType || "widget"}`
              : "Layer"
  );

  const iconFor = (el) => {
    if (el.kind === "text")   return <Type className="h-3.5 w-3.5" />;
    if (el.kind === "image")  return <ImageIcon className="h-3.5 w-3.5" />;
    if (el.kind === "video")  return <Film className="h-3.5 w-3.5" />;
    if (el.kind === "shape")  return <Square className="h-3.5 w-3.5" />;
    if (el.kind === "widget") return <Cloud className="h-3.5 w-3.5" />;
    return <Layers className="h-3.5 w-3.5" />;
  };

  const commitReorder = (targetId) => {
    if (!dragId || !targetId || dragId === targetId) return;
    const ids = rows.map((r) => r.id);
    const from = ids.indexOf(dragId);
    const to   = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, dragId);
    onReorder(next);
  };

  return (
    <div className="w-[280px] shrink-0 border-l border-slate-200 bg-white flex flex-col" data-testid="layers-panel">
      {/* Header */}
      <div className="h-12 px-4 flex items-center justify-between border-b border-slate-200 sticky top-0 bg-white z-10">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-slate-700" />
          <span className="text-sm font-bold text-slate-900">Layers</span>
          <span className="text-xs text-slate-400">{rows.length}</span>
        </div>
        <button
          onClick={onClose}
          className="h-7 w-7 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500"
          title="Close"
          data-testid="layers-close-btn"
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Empty state */}
      {rows.length === 0 && (
        <div className="p-6 text-center text-xs text-slate-400">
          Add text, images or shapes to your design — they&apos;ll appear here.
        </div>
      )}

      {/* Rows */}
      <div className="flex-1 overflow-y-auto py-1">
        {rows.map((el) => {
          const isSel = selectedId === el.id;
          const isDragTarget = dragOver === el.id;
          return (
            <div
              key={el.id}
              draggable
              onDragStart={() => setDragId(el.id)}
              onDragEnter={() => setDragOver(el.id)}
              onDragOver={(e) => { e.preventDefault(); }}
              onDragEnd={() => { setDragId(null); setDragOver(null); }}
              onDrop={(e) => { e.preventDefault(); commitReorder(el.id); setDragId(null); setDragOver(null); }}
              onClick={() => onSelect(el.id)}
              className={`group flex items-center gap-1.5 px-2 py-1.5 mx-1 my-0.5 rounded-md cursor-pointer border ${
                isSel
                  ? "bg-sky-50 border-sky-200"
                  : isDragTarget
                    ? "bg-slate-100 border-sky-300 border-dashed"
                    : "border-transparent hover:bg-slate-50"
              }`}
              data-testid={`layer-row-${el.id}`}
            >
              <GripVertical className="h-3.5 w-3.5 text-slate-300 shrink-0 cursor-grab" />
              <div className={`h-6 w-6 rounded-md flex items-center justify-center shrink-0 ${
                isSel ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-600"
              }`}>
                {iconFor(el)}
              </div>
              {renaming === el.id ? (
                <input
                  autoFocus
                  defaultValue={el.layerName || humanKind(el)}
                  onBlur={(e) => { onRename(el.id, e.target.value); setRenaming(null); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { onRename(el.id, e.target.value); setRenaming(null); }
                    if (e.key === "Escape") setRenaming(null);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 h-6 px-1.5 text-xs border border-sky-300 rounded"
                  data-testid={`layer-rename-input-${el.id}`}
                />
              ) : (
                <div
                  className="flex-1 min-w-0 text-xs font-medium text-slate-700 truncate"
                  onDoubleClick={(e) => { e.stopPropagation(); setRenaming(el.id); }}
                  data-testid={`layer-name-${el.id}`}
                >
                  {humanKind(el)}
                </div>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); onToggleLock(el.id); }}
                className="h-6 w-6 rounded-md hover:bg-slate-200 flex items-center justify-center text-slate-500 opacity-0 group-hover:opacity-100"
                title={el.locked ? "Unlock" : "Lock"}
                data-testid={`layer-lock-${el.id}`}
              >
                {el.locked ? <Lock className="h-3.5 w-3.5 text-rose-500" /> : <Unlock className="h-3.5 w-3.5" />}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDuplicate(el.id); }}
                className="h-6 w-6 rounded-md hover:bg-slate-200 flex items-center justify-center text-slate-500 opacity-0 group-hover:opacity-100"
                title="Duplicate"
                data-testid={`layer-dup-${el.id}`}
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(el.id); }}
                className="h-6 w-6 rounded-md hover:bg-rose-50 flex items-center justify-center text-rose-500 opacity-0 group-hover:opacity-100"
                title="Delete"
                data-testid={`layer-delete-${el.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>

      <div className="border-t border-slate-200 px-3 py-2 text-[10px] text-slate-400">
        Drag rows to reorder · double-click to rename
      </div>
    </div>
  );
}

